import { createContext, useContext } from 'react';
import { plainTextLength } from '@worldcoo/shared/core';
import { cn } from '../../lib/utils';

export interface RecommendedLength {
	min: number;
	max: number;
}

/**
 * The counter is read by NGOs in their own language, but this package has no lingui of its own —
 * apps translate it the same way they do the editor toolbar and the "Optional" label, by filling
 * the provider. The whole sentence is built by the app rather than just the word "characters", so
 * a translation can move the count around instead of being pinned to English word order.
 */
export type CharacterCountLabel = (args: { count: number; min: number; max: number }) => string;

const defaultCharacterCountLabel: CharacterCountLabel = ({ count, min, max }) => `${count}/${min}-${max} characters`;

const CharacterCountLabelContext = createContext(defaultCharacterCountLabel);
export const CharacterCountLabelProvider = CharacterCountLabelContext.Provider;

export function CharacterCount(props: { value: string; recommended: RecommendedLength; isHtml?: boolean }) {
	// Shared with the validators that enforce these limits, so the counter can never say a value
	// fits while the schema rejects it.
	const count = props.isHtml ? plainTextLength(props.value) : props.value.length;
	const { min, max } = props.recommended;
	const isUnder = count < min;
	const isOver = count > max;
	const isInRange = !isUnder && !isOver;
	const label = useContext(CharacterCountLabelContext);

	return (
		<p
			className={cn(
				'm-2 text-xs',
				isInRange ? 'text-emerald-600' : isOver ? 'text-destructive' : 'text-muted-foreground',
			)}
		>
			{label({ count, min, max })}
		</p>
	);
}
